import React, { useState, useRef } from 'react';

function PhotoUploadModal({ isOpen, onClose, stationNo, damageRecords, currentRecordIndex, onUploadSuccess }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [label, setLabel] = useState('');
  const [selectedRecord, setSelectedRecord] = useState('general');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    setError('');

    if (!selected) {
      setFile(null);
      setPreview(null);
      return;
    }

    if (!allowedTypes.includes(selected.type)) {
      setError('Please select an image file (JPEG, PNG, GIF, WebP, or BMP)');
      setFile(null);
      setPreview(null);
      return;
    }

    if (selected.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      setFile(null);
      setPreview(null);
      return;
    }

    // Compress and resize the image
    compressImage(selected).then(compressed => {
      setFile(compressed);
      setPreview(URL.createObjectURL(compressed));
    }).catch(() => {
      // Fallback to original if compression fails
      setFile(selected);
      setPreview(URL.createObjectURL(selected));
    });
  };

  const compressImage = (imageFile) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Target max dimension of 1600px (good detail without excessive size)
        const maxDim = 1600;
        let { width, height } = img;

        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressed = new File([blob], imageFile.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressed);
            } else {
              reject(new Error('Compression failed'));
            }
          },
          'image/jpeg',
          0.82 // Quality: good balance of size and detail
        );
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(imageFile);
    });
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a photo to upload');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('stationNo', stationNo);
      formData.append('label', label.trim());

      if (selectedRecord !== 'general') {
        formData.append('fdsRecordId', selectedRecord);
      }

      const response = await fetch('/api/damage-photos/upload', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Upload failed');
      }

      // Reset form and notify parent
      setFile(null);
      setPreview(null);
      setLabel('');
      setSelectedRecord('general');
      if (fileInputRef.current) fileInputRef.current.value = '';

      if (onUploadSuccess) onUploadSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setPreview(null);
      setLabel('');
      setSelectedRecord('general');
      setError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Upload Damage Photo</h3>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Photo
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
              onChange={handleFileChange}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <p className="text-xs text-gray-400 mt-1">JPEG, PNG, GIF, WebP or BMP. Max 10MB. Images will be optimized for storage.</p>
          </div>

          {/* Preview */}
          {preview && (
            <div className="flex justify-center">
              <img
                src={preview}
                alt="Preview"
                className="max-h-48 rounded-lg border border-gray-200"
              />
            </div>
          )}

          {/* Label / Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label / Description
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={uploading}
              placeholder="e.g. Loop damage close-up, Plinth crack"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              maxLength={255}
            />
          </div>

          {/* Damage Record Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Link to Damage Record
            </label>
            <select
              value={selectedRecord}
              onChange={(e) => setSelectedRecord(e.target.value)}
              disabled={uploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
            >
              <option value="general">General (not linked to specific record)</option>
              {damageRecords && damageRecords.map((record, idx) => {
                const date = record.Date ? new Date(record.Date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : 'No date';
                const type = record.DamageType || 'Unknown type';
                return (
                  <option key={idx} value={record.originalIndex !== undefined ? record.originalIndex : idx}>
                    Record {idx + 1}: {type} — {date}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={handleClose}
            disabled={uploading}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || !file}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {uploading ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Uploading...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Upload Photo
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PhotoUploadModal;
